import './styles.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { DEFAULT_LIMIT } from '../../../helpers/settings';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { classNames } from '../../../utils/api';
import { Defaults, TextEx } from '../../common';
import { DOTS, usePagination } from './usePagination';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number | string) => void;
  mobileBreak?: string;
  className?: string;
  limit?: number;
  isNextPrev?: boolean;
  nextWillBeDisabled?: boolean;
  rows?: Record<string, any>[];
}

const Pagination = (props: PaginationProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: 1200px)`);
  const ref = useRef();
  const {
    onPageChange,
    totalPages,
    currentPage,
    limit,
    className,
    nextWillBeDisabled,
    isNextPrev,
    rows,
  } = props;
  const paginationRange = usePagination({
    currentPage,
    totalPages,
    limit,
    ref,
  });

  const [nextDisabled, setNextDisabled] = useState(false);

  useEffect(() => {
    if (isNextPrev) {
      !nextWillBeDisabled && setNextDisabled(false);
      nextWillBeDisabled &&
        rows &&
        rows.length < (limit ?? DEFAULT_LIMIT) &&
        setNextDisabled(true);
    }
  }, [nextWillBeDisabled]);

  if ((currentPage === 0 || paginationRange.length < 2) && !isNextPrev) {
    return null;
  }

  const onNext = () => {
    setNextDisabled(!!nextWillBeDisabled);
    onPageChange(props.isNextPrev ? 'next' : currentPage + 1);
  };

  const onPrevious = () => {
    setNextDisabled(false);
    onPageChange(props.isNextPrev ? 'prev' : currentPage - 1);
  };

  return (
    <PaginationContainer ref={ref}>
      <PaginationButton
        className={classNames(
          'pagination-button',
          currentPage === 1 && 'disabled',
          isMobile && 'mobile',
        )}
        onClick={onPrevious}>
        <div className="arrow left" />
      </PaginationButton>
      <ul className={classNames('pagination-container', className)}>
        {!props.isNextPrev &&
          paginationRange.map((pageNumber, index) => {
            if (pageNumber === DOTS) {
              return (
                <li
                  key={index + pageNumber}
                  className="pagination-item dots"
                  style={{
                    color: Defaults.fontColor,
                  }}>
                  &#8230;
                </li>
              );
            }

            return (
              <PaginationButton
                key={pageNumber}
                className={classNames(
                  'pagination-item',
                  pageNumber === currentPage && 'selected',
                )}
                onClick={() => onPageChange(pageNumber)}>
                <span style={{ color: Defaults.fontColor }}>{pageNumber}</span>
              </PaginationButton>
            );
          })}
      </ul>
      <PaginationButton
        className={classNames(
          'pagination-button',
          ((currentPage === totalPages && !isNextPrev) || nextDisabled) && 'disabled',
          isMobile && 'mobile',
        )}
        onClick={onNext}>
        <div className="arrow right" />
      </PaginationButton>
    </PaginationContainer>
  );
};

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  width: 100%;

  @media (max-width: 640px) {
    padding: 1rem 1rem;
  }
`;

const PaginationButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${Defaults.grayedFontColor};

  &.selected {
    background-color: ${Defaults.mainBgColor};
    color: ${Defaults.whiteColor};
  }

  &.disabled {
    background-color: ${Defaults.mainBgColor};
  }
`;

export default Pagination;
